import React from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Tree as AntTree } from 'antd';
import type { AntTreeNodeProps, TreeProps as AntTreeProps, DataNode, EventDataNode } from 'antd/es/tree';

import { COLOR } from '@/shared/design-tokens/design-tokens';
import type { TreeNode } from '@/shared/lib/tree';
import { Icon } from '@/shared/ui/Icon';

import styles from './Tree.module.css';

export type TreeProps = Omit<AntTreeProps, 'treeData' | 'onSelect'> & {
  treeData: TreeNode[];
  loadingKeys?: React.Key[];
  showCount?: boolean;
  onSelect?: (
    selectedKeys: React.Key[],
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode<DataNode>;
      selectedNodes: DataNode[];
      nativeEvent: MouseEvent;
    }
  ) => void;
};

const transformTreeData = (treeData: TreeNode[]): DataNode[] => {
  const transformNode = (node: TreeNode): DataNode => {
    const title =
      node.count && node.count > 0 ? (
        <>
          <span>{node.title}</span>
          <span className={styles.treeCount}>{node.count}</span>
        </>
      ) : (
        <span>{node.title}</span>
      );

    return {
      ...node,
      title,
      children: node.children?.map(transformNode),
    } as DataNode;
  };

  return treeData.map(transformNode);
};

export const Tree: React.FC<TreeProps> = ({
  treeData,
  onSelect,
  loadingKeys = [],
  defaultExpandAll = false,
  showLine = false,
  showIcon = false,
  showCount = false,
  ...props
}) => {
  const switcherIcon = (nodeProps: AntTreeNodeProps) => {
    const nodeKey = nodeProps.data?.key;
    if (nodeKey && loadingKeys.includes(nodeKey)) {
      return <LoadingOutlined style={{ fontSize: 14 }} spin />;
    }

    const { expanded } = nodeProps;
    return expanded ? (
      <Icon name="tree-collapse" size={16} color={COLOR.GRAY[0]} />
    ) : (
      <Icon name="tree-expand" size={16} color={COLOR.GRAY[0]} />
    );
  };

  const transformedTreeData = showCount ? transformTreeData(treeData) : treeData;

  return (
    <AntTree
      className={styles.tree}
      treeData={transformedTreeData}
      defaultExpandAll={defaultExpandAll}
      showLine={showLine}
      showIcon={showIcon}
      switcherIcon={switcherIcon}
      onSelect={onSelect}
      {...props}
    />
  );
};
